import React, { useRef, useState } from "react";
import { IconCheck, IconCloudUpload, IconFileText, IconX, IconChevronLeft, IconChevronRight } from "@tabler/icons";
import { count, formatBytes } from "./format";

// Feedback and flow: Stepper, FileDrop, Skeleton, Pagination, OffsetPager.

/* ------------------------------------------------------------------ */
/* Stepper                                                             */
/* ------------------------------------------------------------------ */

// Wizard progress. steps: [{ key, label, description? }]; current: index of
// the active step. onStepClick(index), if given, makes completed steps
// clickable (going back); later steps are never clickable.
export function Stepper({ steps, current = 0, onStepClick, ariaLabel = "Progress", className = "" }) {
  return (
    <ol className={`pa-stepper ${className}`} aria-label={ariaLabel}>
      {steps.map((s, i) => {
        const state = i < current ? "done" : i === current ? "current" : "todo";
        const clickable = onStepClick && i < current;
        const inner = (
          <>
            <span className="pa-step-dot" aria-hidden="true">
              {state === "done" ? <IconCheck size={14} stroke={3} /> : i + 1}
            </span>
            <span className="pa-step-text">
              <span className="pa-step-label">{s.label}</span>
              {s.description && <span className="pa-step-desc">{s.description}</span>}
            </span>
          </>
        );
        return (
          <li key={s.key || i} className={`pa-step is-${state}`} aria-current={state === "current" ? "step" : undefined}>
            {clickable ? (
              <button type="button" className="pa-step-btn" onClick={() => onStepClick(i)}>
                {inner}
              </button>
            ) : (
              <div className="pa-step-btn">{inner}</div>
            )}
          </li>
        );
      })}
      <li className="pa-step-mobile" aria-hidden="true">
        Step {Math.min(current + 1, steps.length)} of {steps.length}
        {steps[current] ? ` · ${steps[current].label}` : ""}
      </li>
    </ol>
  );
}

/* ------------------------------------------------------------------ */
/* FileDrop                                                            */
/* ------------------------------------------------------------------ */

// File extensions of the MIME types the admin pages accept. Used when the
// browser reports no usable type (File.type is "" for some drag sources and
// unregistered extensions, "application/octet-stream" for others, and Windows
// with Excel installed reports .csv as "application/vnd.ms-excel").
const MIME_EXTENSIONS = {
  "application/pdf": [".pdf"],
  "text/csv": [".csv"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/zip": [".zip"],
  "text/plain": [".txt"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
};
const VAGUE_TYPES = ["", "application/octet-stream"];

const extensionsFor = (mime) =>
  mime.endsWith("/*")
    ? Object.keys(MIME_EXTENSIONS)
        .filter((m) => m.startsWith(mime.slice(0, -1)))
        .reduce((all, m) => all.concat(MIME_EXTENSIONS[m]), [])
    : MIME_EXTENSIONS[mime] || [];

// true when `file` matches an accept string like ".pdf,application/pdf,image/*".
// A MIME entry also matches by extension when the reported type is empty or
// generic (and .csv for text/csv whatever Windows reports), so prefer passing
// both forms anyway: ".pdf,application/pdf".
export function acceptsFile(file, accept) {
  if (!accept) return true;
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  const byExtension = (mime) => extensionsFor(mime).some((ext) => name.endsWith(ext));
  return accept
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
    .some((t) => {
      if (t.startsWith(".")) return name.endsWith(t);
      if (t.endsWith("/*") ? type.startsWith(t.slice(0, -1)) : type === t) return true;
      if (VAGUE_TYPES.includes(type) || t === "text/csv") return byExtension(t);
      return false;
    });
}

// Drag-and-drop (or click / Enter to browse) file picker.
//   accept (e.g. ".pdf,application/pdf": give the extension too), multiple, maxSize (bytes), disabled
//   onFiles(accepted: File[], rejected: [{ file, reason }])
//   files + onRemove(index): optional list of chosen files to show below
//   label / hint: the text inside the drop zone
export function FileDrop({
  id,
  accept,
  multiple = false,
  maxSize,
  disabled = false,
  onFiles,
  files,
  onRemove,
  label,
  hint,
  compact = false,
  className = "",
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);

  const handle = (list) => {
    const all = Array.from(list || []);
    const accepted = [];
    const rejected = [];
    all.forEach((file) => {
      if (!acceptsFile(file, accept)) rejected.push({ file, reason: "Unsupported file type" });
      else if (maxSize && file.size > maxSize) rejected.push({ file, reason: `Larger than ${formatBytes(maxSize)}` });
      else accepted.push(file);
    });
    const chosen = multiple ? accepted : accepted.slice(0, 1);
    if (!multiple && accepted.length > 1) accepted.slice(1).forEach((file) => rejected.push({ file, reason: "Only one file can be added" }));
    if (onFiles) onFiles(chosen, rejected);
  };

  const browse = () => !disabled && inputRef.current && inputRef.current.click();

  const drag = {
    onDragEnter: (e) => {
      e.preventDefault();
      if (disabled) return;
      depth.current += 1;
      setDragging(true);
    },
    onDragOver: (e) => {
      e.preventDefault();
      if (!disabled) e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e) => {
      e.preventDefault();
      depth.current = Math.max(0, depth.current - 1);
      if (!depth.current) setDragging(false);
    },
    onDrop: (e) => {
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      if (!disabled) handle(e.dataTransfer.files);
    },
  };

  return (
    <div className={`pa-filedrop-wrap ${className}`}>
      <div
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        className={`pa-filedrop ${dragging ? "is-dragging" : ""} ${disabled ? "is-disabled" : ""} ${compact ? "is-compact" : ""}`}
        onClick={(e) => e.target !== inputRef.current && browse()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            browse();
          }
        }}
        {...drag}
      >
        <span className="pa-filedrop-icon" aria-hidden="true">
          <IconCloudUpload size={compact ? 20 : 26} stroke={1.6} />
        </span>
        <span className="pa-filedrop-text">
          {label || (
            <>
              <strong>{multiple ? "Drop files here" : "Drop a file here"}</strong> or <span className="pa-link">browse</span>
            </>
          )}
        </span>
        {(hint || maxSize) && <span className="pa-filedrop-hint">{hint || `Up to ${formatBytes(maxSize)}`}</span>}
        <input
          ref={inputRef}
          type="file"
          className="pa-visually-hidden"
          tabIndex={-1}
          aria-hidden="true"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => {
            handle(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      {files && files.length > 0 && (
        <ul className="pa-filelist">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              <IconFileText size={18} stroke={1.6} aria-hidden="true" />
              <span className="pa-filelist-name" title={f.name}>
                {f.name}
              </span>
              <span className="pa-filelist-size">{formatBytes(f.size)}</span>
              {onRemove && (
                <button type="button" className="pa-btn pa-btn-icon pa-btn-ghost pa-btn-icon-sm" aria-label={`Remove ${f.name}`} title="Remove" onClick={() => onRemove(i)}>
                  <IconX size={14} stroke={2} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Skeleton                                                            */
/* ------------------------------------------------------------------ */

// Loading placeholder. lines: n text lines (the last one shorter);
// otherwise a block of width x height. circle: round (avatars).
export const Skeleton = ({ width = "100%", height = 14, lines, circle = false, radius, className = "", style }) => {
  if (lines) {
    return (
      <div className={`pa-skel-lines ${className}`} aria-hidden="true" style={style}>
        {Array.from({ length: lines }).map((_, i) => (
          <span key={i} className="pa-skel" style={{ width: i === lines - 1 && lines > 1 ? "60%" : "100%", height }} />
        ))}
      </div>
    );
  }
  return (
    <span
      className={`pa-skel ${className}`}
      aria-hidden="true"
      style={{ width, height, borderRadius: circle ? "50%" : radius, ...style }}
    />
  );
};

/* ------------------------------------------------------------------ */
/* Pagination                                                          */
/* ------------------------------------------------------------------ */

// Page numbers with ellipses: 1 … 4 5 6 … 20
function pageItems(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const set = new Set([1, pages, page - 1, page, page + 1]);
  const list = [...set].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  const out = [];
  list.forEach((p, i) => {
    if (i && p - list[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
}

// For server-side paged lists (the DataGrid pages its own rows).
// page is 1-based; onChange(page). pageSizeOptions + onPageSizeChange(size)
// add a rows-per-page select.
export function Pagination({ page = 1, pageSize = 50, total = 0, onChange, pageSizeOptions, onPageSizeChange, ariaLabel = "Pagination", className = "" }) {
  const pages = Math.max(1, Math.ceil(Number(total) / Number(pageSize)));
  const cur = Math.min(Math.max(1, page), pages);
  const from = total ? (cur - 1) * pageSize + 1 : 0;
  const to = Math.min(total, cur * pageSize);
  const go = (p) => p !== cur && p >= 1 && p <= pages && onChange && onChange(p);
  return (
    <nav className={`pa-pagination ${className}`} aria-label={ariaLabel}>
      <div className="pa-pagination-info">
        <span>
          <span className="pa-num">
            {count(from)}–{count(to)}
          </span>{" "}
          of <span className="pa-num">{count(total)}</span>
        </span>
        {pageSizeOptions && onPageSizeChange && (
          <label className="pa-pagination-size">
            <span>Rows</span>
            <select className="pa-input pa-select" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="pa-pagination-pages">
        <button type="button" className="pa-page-btn" onClick={() => go(cur - 1)} disabled={cur <= 1} aria-label="Previous page">
          <IconChevronLeft size={16} stroke={2} />
        </button>
        {pageItems(cur, pages).map((p) =>
          typeof p === "string" ? (
            <span key={p} className="pa-page-gap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`pa-page-btn ${p === cur ? "is-active" : ""}`}
              aria-current={p === cur ? "page" : undefined}
              aria-label={`Page ${p}`}
              onClick={() => go(p)}
            >
              {p}
            </button>
          )
        )}
        <button type="button" className="pa-page-btn" onClick={() => go(cur + 1)} disabled={cur >= pages} aria-label="Next page">
          <IconChevronRight size={16} stroke={2} />
        </button>
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* OffsetPager                                                         */
/* ------------------------------------------------------------------ */

// Pager for limit/offset APIs that don't return a total (PRF connect).
// "Next" is available while the last page came back full.
//   offset, limit, rowCount (rows on this page), loading
//   onChange(nextOffset), onLimitChange(limit), limitOptions
export function OffsetPager({ offset = 0, limit = 50, rowCount = 0, loading, onChange, onLimitChange, limitOptions = [20, 40, 60, 100], ariaLabel = "Pages", className = "" }) {
  const page = Math.floor(offset / limit) + 1;
  const from = rowCount ? offset + 1 : 0;
  const to = offset + rowCount;
  const hasPrev = offset > 0;
  const hasNext = rowCount >= limit;
  const options = limitOptions.includes(Number(limit)) ? limitOptions : [...limitOptions, Number(limit)].sort((a, b) => a - b);
  return (
    <nav className={`pa-offset-pager ${className}`} aria-label={ariaLabel}>
      <span className="pa-offset-pager-info">
        {rowCount ? (
          <>
            Rows{" "}
            <span className="pa-num">
              {count(from)}–{count(to)}
            </span>
          </>
        ) : (
          "No rows"
        )}
        <span aria-hidden="true">·</span> Page <span className="pa-num">{page}</span>
      </span>
      {onLimitChange && (
        <label className="pa-pagination-size">
          <span>Per page</span>
          <select className="pa-input pa-select" value={limit} disabled={loading} onChange={(e) => onLimitChange(Number(e.target.value))}>
            {options.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}
      <span className="pa-offset-pager-btns">
        <button type="button" className="pa-page-btn" onClick={() => onChange(Math.max(0, offset - limit))} disabled={loading || !hasPrev} aria-label="Previous page">
          <IconChevronLeft size={16} stroke={2} />
        </button>
        <button type="button" className="pa-page-btn" onClick={() => onChange(offset + limit)} disabled={loading || !hasNext} aria-label="Next page">
          <IconChevronRight size={16} stroke={2} />
        </button>
      </span>
    </nav>
  );
}
